import React, { useCallback, useMemo } from 'react';
import moment from 'moment';
import { ControlProps, SegmentedControl } from 'new/elements/SegmentControl/SegmentControl';
import { DateRangePicker, DateRangeInterval } from 'new/elements/DateRangePicker/DateRangePicker';
import {
  getPresetDateRange,
  identifyPreset,
  MetricsDateRange,
  MetricsPresetKey,
  MetricsRangeKey,
} from '../dateRanges';
import styles from './MetricsTrendPanel.module.scss';

const presetControls: ControlProps[] = [
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
  { label: '1y', value: '1y' },
];

const PRESET_KEYS = new Set<string>(presetControls.map((c) => c.value));

const toDate = (value: string | Date) => moment(value).toDate();

function presetTitle(key: MetricsPresetKey): string {
  switch (key) {
    case '7d':
      return 'Last 7 days';
    case '30d':
      return 'Last 30 days';
    case '90d':
      return 'Last 90 days';
    case '1y':
      return 'Last 1 year';
  }
}

export type MetricsDateControlsProps = {
  start: string | Date;
  end: string | Date;
  onDatesChanged: (dates: MetricsDateRange) => void;
  className?: string;
  presets?: MetricsPresetKey[];
};

export const MetricsDateControls = ({
  start,
  end,
  onDatesChanged,
  className,
  presets,
}: MetricsDateControlsProps) => {
  const visibleControls = useMemo(
    () =>
      presets
        ? presetControls.filter((c) => presets.includes(c.value as MetricsPresetKey))
        : presetControls,
    [presets],
  );

  const selectedRangeKey: MetricsRangeKey = useMemo(
    () => identifyPreset(toDate(start), toDate(end)),
    [start, end],
  );

  const presetValue = PRESET_KEYS.has(selectedRangeKey) ? selectedRangeKey : false;

  const emitDatesIfChanged = useCallback(
    (next: { start: Date; end: Date }) => {
      const sameStart = moment(start).isSame(next.start, 'day');
      const sameEnd = moment(end).isSame(next.end, 'day');
      if (sameStart && sameEnd) {
        return;
      }
      onDatesChanged({ start: next.start, end: next.end });
    },
    [start, end, onDatesChanged],
  );

  const onPresetChange = useCallback(
    (_event: React.SyntheticEvent, value: MetricsRangeKey) => {
      if (!value || value === 'custom') {
        return;
      }
      emitDatesIfChanged(getPresetDateRange(value as MetricsPresetKey));
    },
    [emitDatesIfChanged],
  );

  const onCustomRangeChange = useCallback(
    (nextStart: Date | null, nextEnd: Date | null) => {
      if (!nextStart || !nextEnd) {
        return;
      }
      emitDatesIfChanged({ start: nextStart, end: nextEnd });
    },
    [emitDatesIfChanged],
  );

  const maxDate = moment.utc().startOf('day').toDate();

  const pickerIntervals: DateRangeInterval[] = visibleControls.map(({ value: key }) => {
    const { start: s, end: e } = getPresetDateRange(key as MetricsPresetKey);
    return { title: presetTitle(key as MetricsPresetKey), key, startDate: s, endDate: e };
  });

  return (
    <div className={className ?? styles.dateControls}>
      <SegmentedControl
        controls={visibleControls}
        defaultValue="7d"
        value={presetValue}
        onControlChange={onPresetChange}
      />

      <DateRangePicker
        startDate={toDate(start)}
        endDate={toDate(end)}
        maxDate={maxDate}
        intervals={pickerIntervals}
        onChange={onCustomRangeChange}
        utc
      />
    </div>
  );
};

MetricsDateControls.displayName = 'MetricsDateControls';
